'use client';
import { jsx, jsxs } from "react/jsx-runtime";
import { cn } from "@tameeni/shared-logic";
import { motion, LazyMotion, domAnimation, AnimatePresence, m, useMotionValue, useTransform, useAnimation, useInView } from "framer-motion";
import React__default, { useRef } from "react";
const createMotionComponent = (component) => motion(component);
const PresenceAnimation = ({ children }) => /* @__PURE__ */ jsx(AnimatePresence, { children });
const ScrollAnimation = ({ children, className }) => {
  const ref = React__default.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  return /* @__PURE__ */ jsx("div", { className, ref, children: React__default.cloneElement(children, {
    animate: isInView ? "visible" : "hidden"
  }) });
};
const EnhancedAnimation = ({
  children,
  useScroll = false,
  variants,
  className,
  ...props
}) => {
  const MotionComponent = createMotionComponent("div");
  const animatedChildren = /* @__PURE__ */ jsx(
    MotionComponent,
    {
      initial: "hidden",
      animate: "visible",
      exit: "hidden",
      variants,
      className: "h-full",
      ...props,
      children
    }
  );
  return /* @__PURE__ */ jsx(PresenceAnimation, { children: useScroll ? /* @__PURE__ */ jsx(ScrollAnimation, { className, children: animatedChildren }) : animatedChildren });
};
const FadeIn = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    },
    transition: { duration: 0.5, ...transition },
    ...props
  }
);
const SlideIn = ({ direction = "left", transition, ...props }) => {
  const directionMap = {
    left: { x: -50 },
    right: { x: 50 },
    up: { y: -50 },
    down: { y: 50 }
  };
  return /* @__PURE__ */ jsx(
    EnhancedAnimation,
    {
      variants: {
        hidden: { ...directionMap[direction], opacity: 0 },
        visible: { x: 0, y: 0, opacity: 1 }
      },
      transition: { type: "spring", stiffness: 100, damping: 15 },
      ...props
    }
  );
};
const Scale = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { scale: 0.8 },
      visible: { scale: 1 }
    },
    transition: { type: "spring", stiffness: 260, damping: 20 },
    ...props
  }
);
const Rotate = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { rotate: -180, opacity: 0 },
      visible: { rotate: 0, opacity: 1 }
    },
    transition: { type: "spring", stiffness: 200, damping: 30 },
    ...props
  }
);
const Bounce = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { y: -50, opacity: 0 },
      visible: { y: 0, opacity: 1 }
    },
    transition: { type: "spring", stiffness: 400, damping: 10 },
    ...props
  }
);
const Shake = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { x: 0 },
      visible: { x: [0, -10, 10, -10, 10, 0] }
    },
    transition: { duration: 0.5, ...transition },
    ...props
  }
);
const Pulse = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { scale: 1 },
      visible: { scale: [1, 1.05, 1] }
    },
    transition: { duration: 0.5, repeat: Infinity, repeatType: "reverse" },
    ...props
  }
);
const Flip = ({ transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { rotateY: 90, opacity: 0 },
      visible: { rotateY: 0, opacity: 1 }
    },
    transition: { type: "spring", stiffness: 300, damping: 20 },
    ...props
  }
);
const StaggerChildren = ({ children, staggerDuration = 0.1, transition, ...props }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDuration
      }
    }
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 }
    }
  };
  return /* @__PURE__ */ jsx(EnhancedAnimation, { variants: containerVariants, ...props, children: React__default.Children.map(children, (child) => /* @__PURE__ */ jsx(motion.div, { variants: itemVariants, children: child })) });
};
const InteractiveScale = ({
  children,
  ...props
}) => /* @__PURE__ */ jsx(
  motion.div,
  {
    whileHover: { scale: 1.05 },
    whileTap: { scale: 0.95 },
    transition: { type: "spring", stiffness: 400, damping: 17 },
    ...props,
    children
  }
);
const Draggable = ({
  children,
  transition,
  ...props
}) => /* @__PURE__ */ jsx(
  motion.div,
  {
    drag: true,
    dragConstraints: { left: -100, right: 100, top: -100, bottom: 100 },
    whileDrag: { scale: 1.1 },
    dragTransition: { bounceStiffness: 600, bounceDamping: 10 },
    ...props,
    children
  }
);
const AnimatedText = ({ text, transition, ...props }) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
    },
    ...props,
    children: text.split("").map((char, index) => /* @__PURE__ */ jsx(
      motion.span,
      {
        variants: {
          hidden: { opacity: 0, y: 50 },
          visible: { opacity: 1, y: 0 }
        },
        children: char
      },
      `${char}-${index}`
    ))
  }
);
const DrawSVG = ({
  children,
  pathLength = 1,
  ...props
}) => {
  if (props.type === "svg") {
    return /* @__PURE__ */ jsx(
      motion.svg,
      {
        initial: { pathLength: 0 },
        animate: { pathLength },
        transition: { duration: 2, ease: "easeInOut" },
        ...props,
        children
      }
    );
  } else {
    return /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 2, ease: "easeInOut" },
        ...props,
        children
      }
    );
  }
};
const Typewriter = ({ text, delay = 50, repeat = false, transition, ...props }) => {
  const characterTransition = {
    duration: delay / 1e3,
    ...repeat && {
      repeat: repeat === true ? Infinity : repeat,
      repeatDelay: 1
    }
  };
  return /* @__PURE__ */ jsx(
    EnhancedAnimation,
    {
      variants: {
        hidden: { width: "100%" },
        visible: {
          width: "100%"
        }
      },
      ...props,
      children: text.split("").map((char, index) => /* @__PURE__ */ jsx(
        motion.span,
        {
          variants: {
            hidden: { opacity: 0, display: "none" },
            visible: {
              opacity: 1,
              display: "inline",
              transition: {
                ...characterTransition,
                delay: index * (delay / 1e3)
              }
            }
          },
          children: char
        },
        `${char}-${index}`
      ))
    }
  );
};
const LoadingDots = ({
  text = "please wait",
  className
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center", className), children: [
    /* @__PURE__ */ jsx("span", { children: text }),
    /* @__PURE__ */ jsx("div", { className: "flex space-x-1 ml-1", children: [0, 1, 2].map((index) => /* @__PURE__ */ jsx(
      motion.span,
      {
        initial: { opacity: 0 },
        animate: { opacity: [0, 1, 0] },
        transition: {
          duration: 1.2,
          repeat: Infinity,
          delay: index * 0.4,
          ease: "easeInOut"
        },
        children: "."
      },
      index
    )) })
  ] });
};
const BlurReveal = ({
  transition,
  ...props
}) => /* @__PURE__ */ jsx(
  EnhancedAnimation,
  {
    variants: {
      hidden: { filter: "blur(10px)", opacity: 0 },
      visible: { filter: "blur(0px)", opacity: 1 }
    },
    transition: { duration: 0.5, ...transition },
    ...props
  }
);
const FlipCard = ({ front, back, transition, ...props }) => {
  const [isFlipped, setIsFlipped] = React__default.useState(false);
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      className: "w-64 h-64 perspective-1000",
      onClick: () => setIsFlipped(!isFlipped),
      ...props,
      children: /* @__PURE__ */ jsxs(
        motion.div,
        {
          className: "w-full h-full relative transform-style-3d transition-all duration-500",
          animate: { rotateY: isFlipped ? 180 : 0 },
          children: [
            /* @__PURE__ */ jsx("div", { className: "absolute w-full h-full backface-hidden", children: front }),
            /* @__PURE__ */ jsx("div", { className: "absolute w-full h-full backface-hidden rotate-y-180", children: back })
          ]
        }
      )
    }
  );
};
const FloatingAnimation = ({
  transition,
  ...props
}) => /* @__PURE__ */ jsx(
  motion.div,
  {
    animate: {
      y: [0, -10, 0]
    },
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
      ...transition
    },
    ...props
  }
);
const GlitchEffect = ({ text, transition, ...props }) => {
  return /* @__PURE__ */ jsxs(motion.div, { className: "relative", ...props, children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        className: "absolute top-0 left-0 text-red-500",
        animate: {
          x: [-2, 2, -2],
          y: [2, -2, 2]
        },
        transition: {
          duration: 0.3,
          repeat: Infinity,
          repeatType: "reverse"
        },
        children: text
      }
    ),
    /* @__PURE__ */ jsx(
      motion.div,
      {
        className: "absolute top-0 left-0 text-blue-500",
        animate: {
          x: [2, -2, 2],
          y: [-2, 2, -2]
        },
        transition: {
          duration: 0.3,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 0.1
        },
        children: text
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "relative", children: text })
  ] });
};
const Drawer = ({ children, isOpen, direction = "left", transition, ...props }) => {
  const variants = {
    open: (height = 1e3) => ({
      clipPath: `circle(${height * 2 + 200}px at ${direction === "left" ? "0 50%" : direction === "right" ? "100% 50%" : direction === "top" ? "50% 0" : "50% 100%"})`,
      transition: {
        type: "spring",
        stiffness: 20,
        restDelta: 2
      }
    }),
    closed: {
      clipPath: `circle(0px at ${direction === "left" ? "0 50%" : direction === "right" ? "100% 50%" : direction === "top" ? "50% 0" : "50% 100%"})`,
      transition: {
        delay: 0.5,
        type: "spring",
        stiffness: 400,
        damping: 40
      }
    }
  };
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      initial: "closed",
      animate: isOpen ? "open" : "closed",
      variants,
      ...props,
      children
    }
  );
};
const Collapse = ({
  children,
  isOpen,
  ...props
}) => {
  return /* @__PURE__ */ jsx(LazyMotion, { features: domAnimation, strict: true, children: /* @__PURE__ */ jsx("div", { "aria-expanded": isOpen, className: props.className, children: /* @__PURE__ */ jsx(AnimatePresence, { initial: false, children: isOpen && /* @__PURE__ */ jsx(
    m.div,
    {
      initial: "collapsed",
      animate: "open",
      exit: "collapsed",
      variants: {
        open: { height: "auto" },
        collapsed: { height: 0 }
      },
      transition: { duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] },
      children
    }
  ) }) }) });
};
const ElasticBounce = ({
  children,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      initial: { scale: 0 },
      animate: { scale: 1 },
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 10,
        mass: 0.5,
        bounce: 0.8
      },
      ...props,
      children
    }
  );
};
const LiquidMotion = ({
  children,
  ...props
}) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [30, -30]);
  const rotateY = useTransform(x, [-100, 100], [-30, 30]);
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      style: {
        perspective: 2e3,
        x,
        y,
        rotateX,
        rotateY,
        // transition: { damping: 20, stiffness: 300 },
        transition: ""
      },
      transition: { type: "spring", damping: 20, stiffness: 300 },
      drag: true,
      dragElastic: 0.12,
      whileTap: { cursor: "grabbing" },
      ...props,
      children: /* @__PURE__ */ jsx(
        motion.div,
        {
          style: {
            width: "100%",
            height: "100%",
            borderRadius: 30,
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          },
          children
        }
      )
    }
  );
};
const MagneticEffect = ({
  children,
  ...props
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const handleMouse = (event) => {
    var _a;
    const rect = (_a = ref.current) == null ? void 0 : _a.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.sqrt(
        Math.pow(event.clientX - centerX, 2) + Math.pow(event.clientY - centerY, 2)
      );
      const maxDistance = Math.max(rect.width, rect.height) / 2;
      const force = Math.min(distance / maxDistance, 1);
      x.set((event.clientX - centerX) * force * 0.1);
      y.set((event.clientY - centerY) * force * 0.1);
    }
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      ref,
      style: { x, y },
      onMouseMove: handleMouse,
      onMouseLeave: handleMouseLeave,
      ...props,
      children
    }
  );
};
const ParticleExplosion = ({ particleCount = 20, transition, ...props }) => {
  const controls = useAnimation();
  const explode = async () => {
    await controls.start((i) => ({
      x: Math.random() * 200 - 100,
      y: Math.random() * 200 - 100,
      opacity: 0,
      scale: 0,
      transition: { duration: 0.5, delay: i * 0.02 }
    }));
    await controls.start({ x: 0, y: 0, opacity: 1, scale: 1 });
  };
  return /* @__PURE__ */ jsxs(motion.div, { className: "relative", onClick: explode, ...props, children: [
    [...Array(particleCount)].map((_, i) => /* @__PURE__ */ jsx(
      motion.div,
      {
        className: "absolute top-1/2 left-1/2 w-2 h-2 bg-blue-500 rounded-full",
        initial: { x: 0, y: 0, opacity: 1, scale: 1 },
        animate: controls,
        custom: i
      },
      i
    )),
    props.children
  ] });
};
const WavyText = ({ text, transition, ...props }) => {
  const characters = text.split("");
  return /* @__PURE__ */ jsx(motion.div, { ...props, children: characters.map((char, index) => /* @__PURE__ */ jsx(
    motion.span,
    {
      animate: {
        y: [0, -10, 0],
        transition: {
          duration: 0.5,
          repeat: Infinity,
          repeatType: "reverse",
          delay: index * 0.1
        }
      },
      style: { display: "inline-block" },
      children: char
    },
    `${char}-${index}`
  )) });
};
export {
  AnimatedText,
  BlurReveal,
  Bounce,
  Collapse,
  Draggable,
  DrawSVG,
  Drawer,
  ElasticBounce,
  FadeIn,
  Flip,
  FlipCard,
  FloatingAnimation,
  GlitchEffect,
  InteractiveScale,
  LiquidMotion,
  LoadingDots,
  MagneticEffect,
  ParticleExplosion,
  Pulse,
  Rotate,
  Scale,
  Shake,
  SlideIn,
  StaggerChildren,
  Typewriter,
  WavyText
};
